import { useMemo } from "react";

import { Locales } from "@simplyk/common";

import { trpc } from "@/common/helpers/trpc";
import { RateOutput } from "@/common/types/trpc";

interface UseFormMembershipInfoParams {
  organizationId: string | undefined;
  locale: Locales;
  hasMemberOnlyRates: boolean;
  isMembershipForm: boolean;
  ticketingId: string | undefined;
}

export const useFormMembershipInfo = ({
  organizationId,
  locale,
  hasMemberOnlyRates,
  isMembershipForm,
  ticketingId,
}: UseFormMembershipInfoParams) => {
  const needsMembershipInfo = isMembershipForm || hasMemberOnlyRates;

  const isEnabled = needsMembershipInfo;

  const { data, isFetched } = trpc.form_getAuthenticatedUserMemberships.useQuery(
    { organizationId: organizationId!, locale },
    { enabled: Boolean(organizationId && isEnabled) }
  );

  const isAuthenticated = data !== null && data !== undefined;
  const memberName = data?.memberName ?? null;
  const memberEmail = data?.memberEmail ?? null;
  const activeMemberships = useMemo(() => data?.memberships ?? [], [data?.memberships]);
  const isReady = !isEnabled || isFetched;

  const allActiveMembershipRateIds = useMemo(
    () => new Set(activeMemberships.map((membership) => membership.rateId)),
    [activeMemberships]
  );

  const membershipsToDisplayOnBanner = useMemo(
    () => (isMembershipForm ? activeMemberships.filter((m) => m.campaignId === ticketingId) : activeMemberships),
    [activeMemberships, isMembershipForm, ticketingId]
  );

  const shouldDisplayMemberBanner = isEnabled && isAuthenticated && membershipsToDisplayOnBanner.length > 0;

  const isMemberOnlyRate = (rate: RateOutput): boolean => {
    return Boolean(rate.isMemberOnly);
  };

  const isRateAccessible = (rate: RateOutput): boolean => {
    if (!isAuthenticated || !isEnabled || !isFetched) {
      return true;
    }
    if (!rate.isMemberOnly) {
      return true;
    }
    return rate.memberOnlyMembershipRateIds?.some((id) => allActiveMembershipRateIds.has(id)) ?? false;
  };

  const getDuplicateMembership = (rate: RateOutput) => {
    if (!isAuthenticated || !isEnabled || !isFetched) {
      return null;
    }
    return activeMemberships.find((m) => m.campaignId === ticketingId && m.rateId === rate.id) ?? null;
  };

  return {
    isReady,
    isAuthenticated,
    memberName,
    memberEmail,
    activeMemberships,
    membershipsToDisplayOnBanner,
    shouldDisplayMemberBanner,
    isRateAccessible,
    getDuplicateMembership,
    isMemberOnlyRate,
  };
};
